/**
 * Backend DuckDB query service.
 *
 * Wraps calls to POST /api/duckdb/query so the frontend can query
 * data that lives in the backend DuckDB cache (instead of WASM).
 */

import { triggerDownload } from '../utils/csv';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export interface BackendQueryResult {
  success: boolean;
  columns: string[];
  rows: Record<string, any>[];
  rowCount: number;
  error?: string;
}

export async function queryBackendDuckDB(
  sql: string,
): Promise<BackendQueryResult> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/duckdb/query`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ sql }),
    });

    if (!response.ok) {
      return { success: false, columns: [], rows: [], rowCount: 0, error: `HTTP ${response.status}` };
    }

    const data = await response.json();
    return {
      success: data.success,
      columns: data.columns || [],
      rows: data.rows || [],
      rowCount: data.row_count || 0,
      error: data.error,
    };
  } catch (err) {
    return {
      success: false,
      columns: [],
      rows: [],
      rowCount: 0,
      error: err instanceof Error ? err.message : 'Unknown error',
    };
  }
}

/**
 * Request body variants for POST /api/download/csv.
 * `recon` is the blessed typed path; `sql` is an escape hatch.
 */
type DownloadBody =
  | {
      session_id: string;
      filename_stem?: string;
      recon: {
        left_table: string;
        right_table: string;
        join_key: string;
        left_label?: string;
        right_label?: string;
      };
    }
  | {
      session_id: string;
      filename_stem?: string;
      sql: string;
    };

/**
 * Stream a CSV from the backend DuckDB cache directly to the user's browser.
 * Reads the response as a Blob (keeps memory low; fetch handles chunking internally)
 * and triggers an `<a download>` click.
 */
export async function downloadServerCsv(
  body: DownloadBody,
  filename: string,
): Promise<{ ok: boolean; error?: string }> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/download/csv`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      let msg = `HTTP ${response.status}`;
      try {
        const err = await response.json();
        if (err && typeof err.error === 'string') msg = err.error;
      } catch {
        // Response wasn't JSON; keep HTTP code as the message.
      }
      return { ok: false, error: msg };
    }
    const blob = await response.blob();
    triggerDownload(blob, filename);
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}
